import { zLiczba } from '../lib/odmien';
import type { WsConnectionState } from '../lib/wsConnectionStatus';

// =============================================================================
// Panel główny — czyste składanie kart z surowych liczników.
//
// Hook `useDashboardStats` tylko pobiera; to, CO z liczb wynika dla człowieka
// (podpis pod liczbą, ton karty, zdania w pasku NEXUS), jest tutaj — bez
// Reacta i bez Supabase, żeby dało się zmierzyć wprost.
// Dlaczego te źródła, a nie `device_pairings`/`neurosense_alerts`:
// docs/02-ARCHITECTURE/PANEL_GLOWNY_PRAWDZIWE_DANE.md §1.2–§2.
// =============================================================================

/** Statusy subskrypcji modułu, które platforma uznaje za „moduł działa”. */
export const STATUSY_MODULU_AKTYWNE = ['active', 'trial', 'trialing'] as const;

export type WagaAlertu = 'critical' | 'warning';

export interface AlertPanelu {
  id: string;
  /** Tytuł zgłoszenia — to jest treść alertu. */
  message: string;
  severity: WagaAlertu;
  status: string;
  device_id: string | null;
  /** Nazwa czujnika z kartoteki; gdy jej nie ma, pokazujemy identyfikator. */
  device_name: string;
  created_at: string;
}

export interface SuroweLiczniki {
  czujniki: number;
  licencjeUrzadzen: number;
  lokalizacjeAktywne: number;
  lokalizacjeWszystkie: number;
  pracownicyAktywni: number;
  kontaPortalu: number;
  alerty: AlertPanelu[];
  modulyAktywne: number;
}

export const PUSTE_LICZNIKI: SuroweLiczniki = {
  czujniki: 0,
  licencjeUrzadzen: 0,
  lokalizacjeAktywne: 0,
  lokalizacjeWszystkie: 0,
  pracownicyAktywni: 0,
  kontaPortalu: 0,
  alerty: [],
  modulyAktywne: 0,
};

export type KluczKarty = 'urzadzenia' | 'lokalizacje' | 'pracownicy' | 'alerty';

export interface KartaPanelu {
  klucz: KluczKarty;
  wartosc: number;
  /** Zdanie pod liczbą — zawsze z pomiaru, nigdy z porównania, którego nie ma. */
  podpis: string;
  ton: 'neutralny' | 'ok' | 'uwaga';
}

function tekst(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * Wiersz RPC `panel_glowny_alerty` → alert. `null` bez identyfikatora — alert,
 * którego nie da się wskazać, lepiej pominąć niż pokazać jako nietykalny.
 * Waga przychodzi z bazy (ta sama reguła co panel NeuroSense i NEXUS);
 * cokolwiek poza `critical` jest ostrzeżeniem.
 */
export function alertZWiersza(w: unknown): AlertPanelu | null {
  if (!w || typeof w !== 'object') return null;
  const z = w as Record<string, unknown>;
  const id = tekst(z.id).trim();
  if (!id) return null;
  const deviceId = tekst(z.device_id) || null;
  return {
    id,
    message: tekst(z.title) || 'Zgłoszenie bez tytułu',
    severity: z.severity === 'critical' ? 'critical' : 'warning',
    status: tekst(z.status),
    device_id: deviceId,
    device_name: tekst(z.device_name) || deviceId || 'Czujnik',
    created_at: tekst(z.created_at),
  };
}

export function alertyZOdpowiedzi(rows: unknown): AlertPanelu[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(alertZWiersza).filter((a): a is AlertPanelu => a !== null);
}

export function zlozKarty(s: SuroweLiczniki): KartaPanelu[] {
  const krytyczne = s.alerty.filter((a) => a.severity === 'critical').length;
  return [
    {
      klucz: 'urzadzenia',
      wartosc: s.czujniki + s.licencjeUrzadzen,
      podpis:
        s.czujniki + s.licencjeUrzadzen === 0
          ? 'brak sparowanych urządzeń'
          : `${zLiczba(s.czujniki, 'czujnik', 'czujniki', 'czujników')} · ${zLiczba(
              s.licencjeUrzadzen,
              'urządzenie',
              'urządzenia',
              'urządzeń'
            )}`,
      ton: 'neutralny',
    },
    {
      klucz: 'lokalizacje',
      wartosc: s.lokalizacjeAktywne,
      podpis:
        s.lokalizacjeWszystkie > s.lokalizacjeAktywne
          ? `z ${s.lokalizacjeWszystkie} w kartotece`
          : s.lokalizacjeAktywne === 0
            ? 'brak lokalizacji'
            : 'wszystkie aktywne',
      ton: 'neutralny',
    },
    {
      klucz: 'pracownicy',
      wartosc: s.pracownicyAktywni,
      podpis: zLiczba(s.kontaPortalu, 'konto w portalu', 'konta w portalu', 'kont w portalu'),
      ton: 'neutralny',
    },
    {
      klucz: 'alerty',
      wartosc: s.alerty.length,
      podpis:
        s.alerty.length === 0
          ? 'brak otwartych alertów'
          : krytyczne > 0
            ? zLiczba(krytyczne, 'krytyczny', 'krytyczne', 'krytycznych')
            : 'bez krytycznych',
      ton: s.alerty.length === 0 ? 'ok' : 'uwaga',
    },
  ];
}

export type EtykietaPolaczenia = 'Online' | 'Offline' | 'Łączenie…';

export interface PasekNexus {
  moduly: string;
  siec: string;
  alerty: string;
  /** Pastylka „Online” pokazuje stan połączenia z żywą siecią, nie literał. */
  polaczony: boolean;
  /**
   * Trzy stany, nie dwa. Brama restartuje się przy każdym wdrożeniu, a backoff
   * wraca do niej w ciągu 30 s — przez ten czas karta pokazywała „Offline”,
   * czyli werdykt, gdy prawdą było „za chwilę”. `Offline` zostaje dla stanów,
   * z których nic samo nie wróci: brak konfiguracji, brak sesji.
   */
  etykieta: EtykietaPolaczenia;
}

export function etykietaPolaczenia(
  polaczony: boolean,
  stan?: WsConnectionState
): EtykietaPolaczenia {
  if (polaczony) return 'Online';
  if (stan === 'connecting' || stan === 'reconnecting') return 'Łączenie…';
  return 'Offline';
}

export function zlozPasekNexus(
  s: SuroweLiczniki,
  polaczony: boolean,
  stan?: WsConnectionState
): PasekNexus {
  const etykieta = etykietaPolaczenia(polaczony, stan);
  return {
    moduly: zLiczba(s.modulyAktywne, 'aktywny moduł', 'aktywne moduły', 'aktywnych modułów'),
    siec:
      etykieta === 'Online'
        ? 'Żywa sieć połączona'
        : etykieta === 'Łączenie…'
          ? 'Łączenie z żywą siecią…'
          : 'Brak połączenia z żywą siecią',
    alerty:
      s.alerty.length === 0
        ? 'Brak otwartych alertów'
        : zLiczba(s.alerty.length, 'otwarty alert', 'otwarte alerty', 'otwartych alertów'),
    polaczony,
    etykieta,
  };
}
